import {
  FilterColumnsArgs,
  GetColumnForNewFilterArgs,
  GRID_CHECKBOX_SELECTION_FIELD,
  GRID_TREE_DATA_GROUPING_FIELD,
  GridApiPro,
  GridColDef,
  GridFilterItem,
  GridRowSelectionModel,
  gridColumnVisibilityModelSelector,
  gridFilterModelSelector,
  gridSortModelSelector,
  gridVisibleColumnFieldsSelector,
} from "@mui/x-data-grid-pro";
import { version as muiDataGridVersion } from "@mui/x-data-grid-pro/package.json";
import { RefObject } from "react";
import { SavedFilterGridState } from "shared";

// Common utils for data grid
const hiddenFields = [
  GRID_TREE_DATA_GROUPING_FIELD,
  GRID_CHECKBOX_SELECTION_FIELD,
];

export const getTogglableColumns = (columns: GridColDef[], neverShowColumns = []) => {
  return columns
    .filter(column => !hiddenFields.includes(column.field))
    .filter(column => !neverShowColumns.includes(column.field))
    .map(column => column.field);
};

// allowNumberRanges lets number columns be filtered twice (e.g. >= 1000 and <= 2000), see numberFilterParams
export const filterColumns = ({ field, columns, currentFilters }: FilterColumnsArgs, dontShow?: string[], allowNumberRanges = false) => {
  // remove already filtered fields from list of columns
  const filteredFields = currentFilters?.map(item => item.field);
  const filterCount = (colField: string) => filteredFields.filter(filteredField => filteredField === colField).length;

  return columns
    .filter(
      colDef =>
        colDef.filterable &&
        (
          colDef.field === field ||
          filterCount(colDef.field) < (allowNumberRanges && colDef.type === `number` ? 2 : 1)
        ) &&
        !dontShow?.includes(colDef.field),
    )
    .map(column => column.field);
};

/**
 * API params for a number column filter. A single filter row sends `key` and
 * `keyOperator`; two rows on the same column (e.g. >= 1000 and <= 2000) send
 * the tightest `keyMin`/`keyMax` bounds instead. An `=` row is both bounds, so
 * conflicting rows (= 5 and = 6) give min > max and match nothing.
 */
export function numberFilterParams<K extends string>(
  items: GridFilterItem[],
  field: string,
  key: K,
  parse: (value: number) => number | undefined = value => value,
) {
  const fieldItems = items.filter(item => item.field === field && item.value !== undefined && item.value !== null && item.value !== ``);
  const single = fieldItems.length === 1 ? fieldItems[0] : undefined;
  const bound = (operator: `>=` | `<=`, pick: (...values: number[]) => number) => {
    const values = fieldItems
      .filter(item => item.operator === operator || item.operator === `=`)
      .map(item => parse(item.value))
      .filter(value => value !== undefined);

    return fieldItems.length > 1 && values.length ? pick(...values) : undefined;
  };

  return {
    [key]: single ? parse(single.value) : undefined,
    [`${key}Operator`]: single?.operator,
    [`${key}Min`]: bound(`>=`, Math.max),
    [`${key}Max`]: bound(`<=`, Math.min),
  } as Record<K | `${K}Min` | `${K}Max`, number | undefined> & Record<`${K}Operator`, `>=` | `<=` | `=` | undefined>;
}

export const getColumnForNewFilter = ({
  currentFilters,
  columns,
}: GetColumnForNewFilterArgs) => {
  const filteredFields = currentFilters?.map(({ field }) => field);
  const columnForNewFilter = columns
    .filter(
      colDef => colDef.filterable && !filteredFields.includes(colDef.field),
    )
    .find(colDef => colDef.filterOperators?.length);

  return columnForNewFilter?.field ?? null;
};

export const commonConfig = {
  // Shared datagrid chrome: distinct gray header row, branded hover/selection.
  // Pair with <DatagridCard> for the card frame around the grid.
  headerStyle: {
    border: `none`,
    '--DataGrid-containerBackground': `var(--color-gray-50)`, // gray-50 header background
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 500,
      fontSize: `11px`,
      textTransform: `uppercase`,
      letterSpacing: `0.05em`,
      color: `var(--color-gray-500)`,
    },
    '& .MuiDataGrid-row:hover': {
      backgroundColor: `var(--datagrid-hover)`, // paidniceGreen-50 / dark navy tint
    },
    '& .MuiDataGrid-row.Mui-selected': {
      backgroundColor: `var(--datagrid-hover)`,
    },
    '& .MuiDataGrid-row.Mui-selected:hover': {
      backgroundColor: `var(--datagrid-hover-strong)`, // paidniceGreen-100 / dark navy tint
    },
    '& .MuiCheckbox-root.Mui-checked, & .MuiCheckbox-root.MuiCheckbox-indeterminate': {
      color: `#386075`, // paidniceGreen-600
    },
  },
  toolbarStyle: {
    '& .MuiDataGrid-toolbar': {
      height: `fit-content`,
      minHeight: `fit-content`,
      paddingTop: `1px`,
    },
  },
};

// Ghost toolbar buttons with a text label ("Filters", "Columns", "Export"...)
export const toolbarButtonSx = {
  paddingX: 1.25,
  paddingY: 0.75,
  gap: 0.75,
  fontSize: `13px`,
  fontWeight: 500,
  color: `var(--color-gray-600)`,
  borderRadius: `6px`,
  textTransform: `none` as const,
  whiteSpace: `nowrap` as const,
};

// Major version of @mui/x-data-grid(-pro) this code exports state for.
// Stored alongside every saved filter blob so they can be migrated or
// invalidated after a MUI major upgrade. Derived from the installed package
// at build time, so it tracks upgrades automatically.
export const MUI_DATAGRID_MAJOR_VERSION = Number(muiDataGridVersion.split(`.`)[0]);

// Snapshot of the grid's filter + sorting + column visibility state in
// GridInitialState shape, for persisting as a saved filter and re-applying
// via restoreState(). Only the visibility model is kept from the columns
// state, so restoring never clobbers column widths/order.
// Reads via selectors instead of apiRef.current.exportState() because the
// export pipeline mutates the filter items in place (delete item.fromInput),
// which throws when the model is controlled from frozen Redux state.
export function exportFilterGridState(apiRef: RefObject<GridApiPro>, extra?: Record<string, unknown>): SavedFilterGridState {
  const filterModel = gridFilterModelSelector(apiRef);
  const sortModel = gridSortModelSelector(apiRef);
  const columnVisibilityModel = gridColumnVisibilityModelSelector(apiRef);

  // Clone to detach from the frozen Redux-controlled models
  return JSON.parse(JSON.stringify({
    filter: { filterModel },
    sorting: { sortModel },
    columns: { columnVisibilityModel },
    extra,
  }));
}

export function onRowSelectionChangeWrapper(model: GridRowSelectionModel, allCurrentRowIds: Array<string | number>, setSelectionModel: (model: GridRowSelectionModel) => void) {
  if (model.type === `exclude`) {
    // When exclude, the IDs will be empty, so flip back to include model
    model = {
      type: `include`,
      ids: new Set([...allCurrentRowIds]),
    };
  }

  setSelectionModel(model);
}

// Saved filter export params plus the visible columns in display order, so a
// scheduled export of the saved filter has the same columns as the table
export function withExportColumns(apiRef: RefObject<GridApiPro>, params?: Record<string, unknown>) {
  return params && { ...params, columns: gridVisibleColumnFieldsSelector(apiRef) };
}

// The list API params a table derives from its grid state, minus paging and org
// context, so a scheduled export of the saved filter can run without the grid
export function toSavedFilterExportParams(params: object): Record<string, unknown> {
  const rest: Record<string, unknown> = { ...params };

  delete rest.page;
  delete rest.limit;
  delete rest.organisationId;

  return JSON.parse(JSON.stringify(rest));
}
